// Shared pieces of the authorizations UI: the status chip, the used/remaining
// bar, and the date formatting. Used by AuthorizationsPage and by PatientAuths
// (which Instance B mounts in the patient drawer), so a chip means the same
// thing in both places.

import React from "react";

export const API_URL = process.env.REACT_APP_API_URL || "";

// Mirrors authorization_service.status_for. The backend computes the state; this
// only decides how it looks, so the two cannot disagree about WHICH state a row
// is in — only about its colour, which is a smaller bug.
export const STATUS_LABELS = {
  active: "Active",
  expiring: "Expiring",
  exhausted: "Exhausted",
  expired: "Expired",
  future: "Not started",
  revoked: "Revoked",
};

export const STATUS_FILTERS = [
  "active",
  "expiring",
  "exhausted",
  "expired",
  "future",
  "revoked",
];

export function StatusChip({ status }) {
  const known = STATUS_LABELS[status] ? status : "unknown";
  return (
    <span className={`auth-chip auth-chip-${known}`}>
      {STATUS_LABELS[status] || status || "—"}
    </span>
  );
}

// used / remaining. `remaining === null` means the auth carries no unit cap —
// uncapped is UNCOUNTED, not zero, and drawing an empty bar for it would read as
// "no visits left" on an authorization that has no limit at all. The Allswell
// tracker's Auths tab has no units column, so this is the normal case for
// sheet-fed practices, not an edge case.
export function UsageBar({ used, remaining, authorizedUnits, status }) {
  // An authorization that has expired or been revoked cannot be drawn down,
  // whatever the arithmetic says. Showing "3 left" in live orange on an expired
  // row invites a biller to bill against it; the number is still shown, because
  // it is true and matters for a re-authorization request, but it is stated as
  // past tense rather than as capacity.
  const spent = status === "expired" || status === "revoked";
  // `used` null means NOT COUNTED (the advisor sends null for an uncapped auth),
  // which is not the same as nothing used. Render "—", never 0.
  const uncounted = used === null || used === undefined;

  if (authorizedUnits === null || authorizedUnits === undefined) {
    return (
      <span
        className="auth-usage-none"
        title="No unit cap on file for this authorization"
      >
        {uncounted ? "—" : used} used · no cap
      </span>
    );
  }
  const total = Number(authorizedUnits) || 0;
  if (uncounted) {
    return (
      <span className="auth-usage-none" title="Usage has not been counted">
        — / {total}
      </span>
    );
  }
  const usedCount = Number(used) || 0;
  const pct = total > 0 ? Math.min(100, Math.round((usedCount / total) * 100)) : 100;
  const level = spent
    ? "done"
    : remaining === 0
      ? "spent"
      : pct >= 80
        ? "low"
        : "ok";
  return (
    <div className="auth-usage">
      <div className="auth-usage-track">
        <div
          className={`auth-usage-fill auth-usage-${level}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`auth-usage-text${spent ? " auth-usage-text-done" : ""}`}>
        {usedCount} / {total}
        <span className="auth-usage-remaining">
          {spent
            ? " · unused"
            : remaining === 0
              ? " · none left"
              : remaining === null || remaining === undefined
                ? " · — left"
                : ` · ${remaining} left`}
        </span>
      </span>
    </div>
  );
}

export function formatWindow(from, to) {
  if (!from && !to) return "No dates";
  const fmt = (d) => (d ? d : "—");
  return `${fmt(from)} → ${fmt(to)}`;
}

// A row a feed owns. Editing it in Atlas would be overwritten by the next sync,
// so the UI says where it came from and does not offer the pencil.
export function SourceBadge({ sourceSystem }) {
  if (!sourceSystem) return null;
  const label = sourceSystem === "cobalt" ? "eCW" : sourceSystem;
  return (
    <span
      className="auth-source-badge"
      title={`Fed by ${label} — edit it there, not here`}
    >
      fed by {label}
    </span>
  );
}

export function errorFrom(response, payload, fallback) {
  if (payload && payload.error) return payload.error;
  return `${fallback} (${response.status})`;
}
